import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { shopDateInputValue, type Dashboard, type SalesReport } from '@tailoring-crm/shared';
import { formatMonth } from '../../lib/format.js';
import { useSalesReport } from '../../lib/reports/use-reports.js';
import { LoadError } from '../feedback/load-error.js';
import { Skeleton, SkeletonBlock, cn } from '@tailoring-crm/ui';
import { BigNumberCard } from './big-number-card.js';
import { DailySalesTrend } from './daily-sales-trend.js';
import { MonthlySalesBars } from './monthly-sales-bars.js';
import { MonthlySalesTable } from './monthly-sales-table.js';
import {
  dailySeries,
  monthToDate,
  monthlyTotals,
  percentChange,
  salesWindow,
} from './sales-series.js';
import { StageDonut } from './stage-donut.js';

/**
 * The analytics grid under the Home tiles: headline sales figures, work in hand as a ring, six
 * months of sales as bars, this month against last day by day, and the months as a table.
 *
 * **Money is asked for only by somebody allowed to see it.** The dashboard answers `money: null`
 * to a caller without the revenue permission, and that is the switch here: without it the grid
 * is the work-in-hand ring and nothing else, and the sales report — which would answer 403 — is
 * never requested at all. A request that is certain to be refused is a red error in somebody's
 * network log and a wasted round trip on a phone.
 *
 * **One request feeds every sales panel.** The sales report for every day from the first of the
 * month five months back to today; `sales-series.ts` cuts the months, the month-to-date figures
 * and the daily lines out of its `byDay` rows. `limit: 1` because Home reads none of the report's
 * leaderboards, so there is no reason to carry ten customers' names over a 4G connection.
 *
 * "Today" is the shop's day (IST), from the shared helper — never the phone's local date, which
 * is a day out for an owner travelling abroad. It is fixed when Home mounts, so the request's key
 * does not change under a page left open across midnight. The figures are then cut against the
 * range the server ECHOES, so even that edge cannot put a day in the wrong month.
 *
 * A failed sales request is an inline error in this grid only: the tiles, the attention list and
 * the work-in-hand ring are a different request, and they keep working.
 */
export function DashboardAnalytics({ dashboard }: { dashboard: Dashboard }): ReactElement {
  const { t } = useTranslation();
  const canSeeMoney = dashboard.money !== null;
  const [today] = useState(() => shopDateInputValue(new Date()));
  const range = salesWindow(today);
  const sales = useSalesReport(
    { preset: 'custom', from: range.from, to: range.to, limit: 1 },
    { enabled: canSeeMoney },
  );

  return (
    <section
      aria-label={t('dashboard.analytics.label')}
      className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3"
    >
      {canSeeMoney && sales.isPending ? <NumbersSkeleton /> : null}
      {canSeeMoney && sales.isError ? (
        <LoadError
          className="col-span-full py-4"
          title={t('dashboard.analytics.loadError')}
          onRetry={() => void sales.refetch()}
        />
      ) : null}
      {canSeeMoney && sales.data ? <SalesNumbers report={sales.data} /> : null}

      <StageDonut workload={dashboard.workload} />

      {canSeeMoney && sales.isPending ? (
        <>
          <PanelSkeleton className="lg:col-span-2" />
          <PanelSkeleton />
          <PanelSkeleton className="lg:col-span-2" />
        </>
      ) : null}
      {canSeeMoney && sales.data ? <SalesCharts report={sales.data} /> : null}
    </section>
  );
}

/** The four headline figures, in a row of their own above the charts. */
function SalesNumbers({ report }: { report: SalesReport }): ReactElement {
  const { t } = useTranslation();
  const today = report.range.toDay;
  const { current, previous } = monthToDate(report.byDay, today);
  const months = monthlyTotals(report.byDay, today);
  const sixMonths = months.reduce((sum, row) => sum + row.revenuePaise, 0);

  return (
    <div className="col-span-full grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <BigNumberCard
        label={t('dashboard.analytics.salesThisMonth')}
        value={{ paise: current.revenuePaise }}
        change={percentChange(current.revenuePaise, previous.revenuePaise)}
      />
      <BigNumberCard
        label={t('dashboard.analytics.ordersThisMonth')}
        value={{ count: current.orderCount }}
        change={percentChange(current.orderCount, previous.orderCount)}
      />
      <BigNumberCard
        label={t('dashboard.analytics.averageOrder')}
        value={{ paise: current.averageOrderPaise }}
        change={percentChange(current.averageOrderPaise, previous.averageOrderPaise)}
      />
      <BigNumberCard
        label={t('dashboard.analytics.salesSixMonths')}
        value={{ paise: sixMonths }}
        caption={t('dashboard.analytics.sinceMonth', {
          month: formatMonth(months[0]!.month, 'long'),
        })}
      />
    </div>
  );
}

/**
 * The trend is wide (thirty-one days need the room) and sits beside the ring; the bars and the
 * table share the row below. On a two-column tablet each takes one column, on a phone they stack.
 */
function SalesCharts({ report }: { report: SalesReport }): ReactElement {
  const today = report.range.toDay;
  const months = monthlyTotals(report.byDay, today);
  return (
    <>
      <DailySalesTrend series={dailySeries(report.byDay, today)} className="lg:col-span-2" />
      <MonthlySalesBars months={months} />
      <MonthlySalesTable months={months} className="lg:col-span-2" />
    </>
  );
}

function NumbersSkeleton(): ReactElement {
  return (
    <SkeletonBlock className="col-span-full grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 4 }, (_, index) => (
        <div
          key={index}
          className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4"
        >
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-3 w-32" />
        </div>
      ))}
    </SkeletonBlock>
  );
}

function PanelSkeleton({ className }: { className?: string }): ReactElement {
  return (
    <SkeletonBlock
      className={cn('flex flex-col gap-3 rounded-lg border border-border bg-card p-4', className)}
    >
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-40 w-full" />
    </SkeletonBlock>
  );
}
